import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Info } from 'lucide-react';
import teamService from 'shared/services/teamService';
import { announceBillingChange } from 'shared/hooks/useBillingV2Status';
import { settingsFocusRing } from 'features/settings/components/settingsTheme';
import { Switch } from 'features/settings/components/SettingsRows';

/**
 * Turn automatic overage on or off — one row inside the plan card.
 *
 * Everything under this already existed (endpoint, plan rules, metered Stripe
 * subscription, the gate that meters instead of blocking, the weekly reporter)
 * and a customer could reach none of it. The only way to arm overage was an
 * UPDATE on teams.auto_usage_enabled.
 *
 * It lives in the plan card rather than a card of its own because it IS a
 * property of the plan: what happens when you reach its limits. The rates sit
 * behind an info icon so the common case — glancing at your plan — stays a
 * clean row, and the pricing is one hover away when you actually care.
 *
 * ONE toggle, not one per metric. auto_usage_enabled arms the org's metered
 * subscription, which carries the units price and the issue-instance price as
 * two items, and the gate reads a single armed flag. A per-metric control
 * without splitting all three would be a switch that silently governs both.
 *
 * No checkout: a paying team's card is already on this Stripe customer, so
 * arming reuses it. When there is genuinely no card the server says so and this
 * points at Manage billing instead of failing.
 */

const API_URL = process.env.REACT_APP_API_SERVER_URL || 'https://backend.trodo.ai';
const INK = 'var(--text-primary)';
const MUTED = 'var(--text-muted)';

function authHeaders() {
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** "$12 per 100,000 units" from the live Stripe price — never hardcoded. */
function ratePhrase(entry, noun) {
  if (!entry || entry.amount == null) return null;
  const amount = `$${Number(entry.amount).toFixed(2).replace(/\.00$/, '')}`;
  const n = Number(entry.per || 1);
  if (n === 1) return `${amount} per ${noun.replace(/s$/, '')}`;
  return `${amount} per ${n.toLocaleString('en-US')} ${noun}`;
}

const RATE_CARD_W = 290;
/** Rough card height, used only to decide whether there is room above. */
const RATE_CARD_H = 200;

/** Brand-styled hover card. Opens on hover AND focus so it is reachable without a mouse. */
function RateInfo({ units, executions, armed }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const triggerRef = useRef(null);
  const timer = useRef(null);

  const show = () => { clearTimeout(timer.current); setOpen(true); };
  // Small delay so crossing the icon on the way somewhere else does not flash it.
  const hide = () => { timer.current = setTimeout(() => setOpen(false), 120); };
  useEffect(() => () => clearTimeout(timer.current), []);

  /**
   * Place the card against the trigger in viewport coordinates.
   *
   * It used to be `position: absolute` inside this row — and this row is the
   * last child of a `settingsCardShell`, which is `overflow-hidden`. A 290px
   * card centred on an icon that sits ~150px from the card's left edge had its
   * left third clipped away, and the portion above the row was cut off
   * entirely, so the rates were unreadable exactly where they were needed.
   * Portaling to <body> with fixed positioning is what every other popover in
   * the app does for this reason.
   */
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const gap = 8;
      const centre = r.left + r.width / 2;
      const maxLeft = Math.max(gap, window.innerWidth - RATE_CARD_W - gap);
      const left = Math.min(Math.max(gap, centre - RATE_CARD_W / 2), maxLeft);
      const above = r.top >= RATE_CARD_H + gap;
      setPos({
        left,
        top: above ? r.top - gap : r.bottom + gap,
        above,
        // Keep the arrow under the icon even after the card is clamped to the
        // viewport, but never let it slide off the card's own corners.
        arrow: Math.min(Math.max(centre - left, 12), RATE_CARD_W - 12),
      });
    };
    place();
    // `true` — capture phase, so scrolling any ancestor repositions it too.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  return (
    <span className="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Overage rates and how this works"
        aria-expanded={open}
        onFocus={show}
        onBlur={hide}
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
        className={`inline-flex items-center justify-center rounded-full p-0.5 transition-colors ${settingsFocusRing}`}
        style={{ color: open ? 'var(--brand-primary)' : MUTED }}
      >
        <Info size={13} />
      </button>

      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          role="tooltip"
          // The card is no longer a DOM descendant of the trigger, so it needs
          // its own hover handlers — without them, moving the pointer off the
          // icon and onto the card dismisses it mid-read.
          onMouseEnter={show}
          onMouseLeave={hide}
          className="w-[290px] rounded-lg p-3 shadow-token-lg"
          style={{
            position: 'fixed',
            left: pos.left,
            top: pos.top,
            transform: pos.above ? 'translateY(-100%)' : undefined,
            zIndex: 1400,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-default)',
          }}
        >
          <p className="text-[11.5px] leading-relaxed" style={{ color: MUTED }}>
            {armed
              ? 'Ingestion continues past your included amounts, billed weekly.'
              : 'Ingestion stops at your included amounts.'}
          </p>

          {(units || executions) && (
            <div
              className="mt-2.5 flex flex-col gap-1 border-t pt-2.5"
              style={{ borderColor: 'var(--border-subtle)' }}
            >
              {units && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11.5px]" style={{ color: MUTED }}>Traces</span>
                  <span className="text-[11.5px] font-medium" style={{ color: INK }}>{units}</span>
                </div>
              )}
              {executions && (
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-[11.5px]" style={{ color: MUTED }}>Executions</span>
                  <span className="text-[11.5px] font-medium" style={{ color: INK }}>{executions}</span>
                </div>
              )}
            </div>
          )}


          {/* Arrow: borders on the two edges facing the trigger. */}
          <span
            className="absolute h-2 w-2 -translate-x-1/2 rotate-45"
            style={{
              left: pos.arrow,
              [pos.above ? 'top' : 'bottom']: 'calc(100% - 5px)',
              background: 'var(--surface-raised)',
              borderRight: pos.above ? '1px solid var(--border-default)' : undefined,
              borderBottom: pos.above ? '1px solid var(--border-default)' : undefined,
              borderLeft: pos.above ? undefined : '1px solid var(--border-default)',
              borderTop: pos.above ? undefined : '1px solid var(--border-default)',
            }}
          />
        </div>,
        document.body,
      )}
    </span>
  );
}

const OverageToggle = ({ teamId, onManageSubscription }) => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [needsCard, setNeedsCard] = useState(false);

  const load = useCallback(async () => {
    if (!teamId) return;
    try {
      const res = await axios.get(`${API_URL}/api/billing/v2/status`, {
        params: { teamId }, headers: authHeaders(), withCredentials: true,
      });
      setStatus(res.data || null);
    } catch {
      // Fail quiet: a status blip should hide this row, not break the plan card.
      setStatus(null);
    }
  }, [teamId]);

  useEffect(() => { load(); }, [load]);

  const overage = status?.overage;
  const plan = status?.plan;

  const setArmed = useCallback(async (enabled) => {
    setBusy(true); setError(null); setNeedsCard(false);
    try {
      await axios.post(
        `${API_URL}/api/billing/v2/overage`,
        { teamId, enabled },
        { headers: authHeaders(), withCredentials: true },
      );
      // Re-read rather than trusting the local guess: arming creates a Stripe
      // subscription, and if that half failed the server is the only thing that
      // knows.
      //
      // Drop the cached billing status first — it is now stale, and the
      // subscription context serves it for the rest of the session otherwise.
      teamService.invalidate(teamId, 'subscription');
      await load();
      announceBillingChange();
    } catch (err) {
      const data = err.response?.data;
      setError(data?.error || err.message || 'Could not change this setting.');
      setNeedsCard(Boolean(data?.needsPaymentMethod));
      await load();
    } finally {
      setBusy(false);
    }
  }, [teamId, load]);

  // Free teams are sold overage by the upgrade card instead — a switch you
  // cannot use is not an upsell.
  if (!status || !overage || plan === 'FREE') return null;

  const units = ratePhrase(status?.pricing?.overage?.units, 'traces');
  const executions = ratePhrase(status?.pricing?.overage?.executions, 'executions');
  const armed = Boolean(overage.armed);
  const locked = !overage.allowed || overage.lockedReason === 'always-on';

  const lockCopy =
    overage.lockedReason === 'always-on'
      ? 'Always on'
      : plan === 'TRIAL'
        ? 'Not available on a trial'
        : null;

  return (
    <div className="flex min-h-[36px] flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-[color:var(--border-subtle)] py-1.5 last:border-b-0">
      <div className="flex items-center gap-1.5">
        <span className="text-[12px]" style={{ color: MUTED }}>Usage beyond plan</span>
        <RateInfo units={units} executions={executions} armed={armed} />
      </div>

      <div className="ml-auto flex items-center gap-2">
        {lockCopy ? (
          <span className="text-[12px]" style={{ color: MUTED }}>{lockCopy}</span>
        ) : (
          <span className="text-[12px]" style={{ color: armed ? 'var(--feedback-success)' : MUTED }}>
            {busy ? 'Saving…' : armed ? 'On' : 'Off'}
          </span>
        )}
        {!locked && (
          <Switch checked={armed} onChange={setArmed} disabled={busy} label="Allow usage beyond my plan" />
        )}
      </div>

      {error && (
        <p className="w-full text-[11px]" style={{ color: 'var(--feedback-error)' }}>
          {error}
          {needsCard && onManageSubscription && (
            <button
              type="button"
              onClick={onManageSubscription}
              className={`ml-1.5 rounded underline ${settingsFocusRing}`}
              style={{ color: 'var(--brand-primary)' }}
            >
              Add a payment method
            </button>
          )}
        </p>
      )}
    </div>
  );
};

export default OverageToggle;
